import { action, computed, flow, makeObservable, override } from 'mobx';
import { RootStore } from './RootStore';
import { ShiftRegistration } from '.';
import { IServerShiftDetails, ShiftDetails } from './models/ShiftDetails';
import { Shift } from './models/Shift';
import { IServerShift, ServerShift } from './models/server/ServerShift';
import { getSupplementalDataRequest, ISupplementalOrganizationData, SupplementalOrganizationDataRequest } from './models/SupplementalOrganizationData';
import { IStatus } from './models/Status';
import { ShiftIdentification } from './models/ShiftIdentification';
import { ShiftDetailsModification } from './models/ShiftDetailsModification';
import { APIError, IAPIError } from './models/APIError';
import { DataStore } from './DataStore';

enum ShiftRequestType {
    SignUpForShift = 'post-shift-registration',
    UnregisterForShifts = 'unregister-for-shifts',
    RegisterVolunteersForShift = 'post-volunteer-registrations',
    GetAllShiftsForOrganization = 'get-all-for-org',
    GetOrganizationVolunteerShifts = 'get-org-vol-shifts',
    GetShiftDetails = 'get-shift-details',
    UpdateVolunteerRegistrationStatuses = 'update-volunteer-registration-statuses',
    PostShift = 'post-shift',
    PutShift = 'put-shift'
}

export class ShiftStore extends DataStore<ShiftRequestType> {
    volunteerRegistrationStatuses: IStatus[] = [];

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this);
    }

    @computed get filteredRegistrationStatuses() {
        return this.volunteerRegistrationStatuses.filter(status => status.status !== 'Unregistered');
    }

    /*************** Sign up for shift ***************/

    signUpForShift = flow(function* (this: ShiftStore, shiftRegistration: ShiftRegistration) {
        try {
            let allRegistrationsValidated = true;
            shiftRegistration.volunteerRegistrations.forEach(volunteerRegistration => {
                volunteerRegistration.setAllFieldsDirty();
                if (!volunteerRegistration.validated) {
                    allRegistrationsValidated = false;
                }
            });
            if (allRegistrationsValidated) {
                const data = {
                    volunteerIds: shiftRegistration.volunteerRegistrations.map(volunteer => volunteer.volunteerId),
                    offsets: shiftRegistration.shiftIdentification.defaultDaysFromStartDate !== undefined ? [shiftRegistration.shiftIdentification.defaultDaysFromStartDate] : []
                }
                const response = yield this.makeRequest<IServerShift | { error: string }>(
                    ShiftRequestType.SignUpForShift,
                    data,
                    { topLevelShiftId: shiftRegistration.shiftIdentification.topLevelId }
                );
                if ('error' in response) {
                    return response;
                }
                return new ServerShift(response).deprecatedDeserialize();
            }
        } catch (error) {
            console.log(error);
        }
    });

    /*************** Unregister for shift ***************/

    unregisterForShifts = flow(function* (this: ShiftStore, shiftIds: ShiftIdentification[], volunteerIds: number[]) {
        try {
            if (this.rootStore.userStore.user.isVolunteerProfileActive) {
                const response: { shifts: IServerShift[] } = yield this.makeRequest<{ shifts: IServerShift[] }>(ShiftRequestType.UnregisterForShifts, { shiftIds: shiftIds, volunteerIds: volunteerIds });
                return { shifts: response.shifts.map(shift => new ServerShift(shift).deprecatedDeserialize()) };
            }
        } catch (error) {
            console.log(error);
        }
    });

    /*************** Register volunteers for shift ***************/

    registerVolunteers = flow(function* (this: ShiftStore, volunteerIds: number[], topLevelShiftId: number, offsets: number[]) {
        if (this.rootStore.userStore.user.isOrganizationProfileActive) {
            const data = {
                volunteerIds: volunteerIds,
                offsets
            }
            const response = yield this.makeRequest<{ shift: IServerShift }>(
                ShiftRequestType.RegisterVolunteersForShift,
                data,
                { topLevelShiftId }
            );
            const shift = new ServerShift(response.shift).deprecatedDeserialize();
            this.assignAddressToShift(shift, response.shift.addressId);
            return shift;
        }
    });

    /*************** Update the status of volunteer registrations for a shift ***************/

    updateVolunteerRegistrationStatuses = flow(function* (this: ShiftStore, volunteerIds: number[], shiftIdentification: ShiftIdentification, statusId: number) {
        try {
            if (this.rootStore.userStore.user.isOrganizationProfileActive) {
                const data = {
                    volunteerIds: volunteerIds,
                    shiftIdentification: shiftIdentification,
                    statusId: statusId
                }
                const response = yield this.makeRequest<{ success: boolean, shift: IServerShift | undefined }>(
                    ShiftRequestType.UpdateVolunteerRegistrationStatuses,
                    data
                );
                return {
                    ...response,
                    shift: response.shift !== undefined ? new ServerShift(response.shift).deprecatedDeserialize() : undefined
                }
            }
        } catch (error) {
            console.log(error);
        }
    });

    /*************** Get all shifts for a particular organization ***************/

    getAllShiftsForOrganization = flow(function* (this: ShiftStore, orgId?: number) {
        try {
            let organizationId = orgId ? orgId : this.rootStore.userStore.user.organization?.id;
            if (organizationId) {
                const supplementalData = getSupplementalDataRequest(
                    ['locations', 'shiftOpportunities', 'roles']
                );

                let response: { shifts: IServerShift[] } = yield this.makeRequest<{ shifts: IServerShift[] }>(
                    ShiftRequestType.GetAllShiftsForOrganization,
                    {
                        id: organizationId,
                        supplementalData: {
                            ...supplementalData,
                            shiftOpportunities: true
                        }
                    },
                    { organizationId }
                );
                return response.shifts.map(shift => new ServerShift(shift).deprecatedDeserialize());
            }
        } catch (error) {
            console.log(error);
        }
    });

    /*************** Get all shifts for a particular volunteer at an organization ***************/

    getShiftsForOrganizationVolunteer = flow(function* (this: ShiftStore, volunteerId: number, requestData: { limit: number, offset: number }) {
        try {
            if (this.rootStore.organizationStore.organization.id) {
                const response: { results: IServerShift[], total: number } = yield this.makeRequest<{ results: IServerShift[], total: number }>(
                    ShiftRequestType.GetOrganizationVolunteerShifts,
                    {
                        volunteerId: volunteerId,
                        ...requestData
                    },
                    { volunteerId }
                );
                if (response) {
                    return {
                        ...response,
                        results: response.results.map(result => new ServerShift(result).deprecatedDeserialize())
                    }
                }
            }
        } catch (error) {
            console.log(error);
        }
    });

    /*************** Get a particular shift ***************/

    getShiftDetails = flow(function* (this: ShiftStore, shiftOrParentId: number, offset?: number) {
        try {
            let supplementalData: SupplementalOrganizationDataRequest = {};
            if (this.rootStore.userStore.user.isOrganizationProfileActive) {
                supplementalData = getSupplementalDataRequest(
                    ['volunteers', 'locations', 'volunteerRegistrationStatuses', 'roles']
                );
            }

            const data = yield this.makeRequest<IServerShiftDetails | { error: IAPIError }>(
                ShiftRequestType.GetShiftDetails,
                { id: shiftOrParentId, supplementalData: supplementalData, offset: offset },
                { shiftOrParentId: shiftOrParentId }
            );

            if ('error' in data) throw new APIError(data.error);

            const shiftDetails = new ShiftDetails(data);
            this.assignAddressToShift(shiftDetails.shift, data.shift.addressId);
            return shiftDetails;
        } catch (error) {
            console.log(error);
            throw error;
        }
    });

    /*************** Update a particular shift ***************/

    postShift = flow(function* (this: ShiftStore, shiftDetailsModification: ShiftDetailsModification) {
        try {
            let data = yield this.makeRequest<IServerShiftDetails & ISupplementalOrganizationData>(
                ShiftRequestType.PostShift,
                shiftDetailsModification.serialize()
            );

            const newDetails = new ShiftDetails(data);
            this.assignAddressToShift(newDetails.shift, data.shift.addressId);
            return { details: newDetails, shiftOpportunities: data.supplementalOrganizationData.shiftOpportunities };
        } catch (error) {
            console.log(error);
        }
    });

    putShift = flow(function* (this: ShiftStore, shiftDetailsModification: ShiftDetailsModification) {
        try {
            const serializedShiftDetailsModification = shiftDetailsModification.serialize();
            const { identificationData } = serializedShiftDetailsModification;
            const topLevelShiftId = identificationData?.parentId ? identificationData.parentId : identificationData?.shiftId;
            let data = yield this.makeRequest<IServerShiftDetails & ISupplementalOrganizationData>(
                ShiftRequestType.PutShift,
                serializedShiftDetailsModification,
                { topLevelShiftId }
            );

            const newDetails = new ShiftDetails(data);
            this.assignAddressToShift(newDetails.shift, data.shift.addressId);
            return { details: newDetails, shiftOpportunities: data.supplementalOrganizationData.shiftOpportunities };
        } catch (error) {
            console.log(error);
        }
    });

    /*************** Private response handling methods ***************/

    @action private assignAddressToShift(shift: Shift, addressId?: number) {
        if (addressId === undefined || shift.address) return;
        const shiftAddress = this.rootStore.userStore.user.organization?.locations.addresses.find(address => address.id === addressId);
        shift.setAddress(shiftAddress);
        shift.modifiedInstances.forEach(modifiedInstance => {
            this.assignAddressToShift(modifiedInstance, modifiedInstance.addressId);
        })
    }

    /*************** Private request handling methods ***************/

    @override get getRequests() {
        return [
            ShiftRequestType.GetAllShiftsForOrganization,
            ShiftRequestType.GetOrganizationVolunteerShifts,
            ShiftRequestType.GetShiftDetails
        ];
    }

    @override get putRequests() {
        return [
            ShiftRequestType.UnregisterForShifts,
            ShiftRequestType.UpdateVolunteerRegistrationStatuses,
            ShiftRequestType.PutShift,
        ];
    }

    @override get postRequests() {
        return [
            ShiftRequestType.SignUpForShift,
            ShiftRequestType.RegisterVolunteersForShift,
            ShiftRequestType.PostShift
        ];
    }

    @override get requestURLs() {
        const baseURL = '/api/shifts'
        const organizationsAPI = '/api/organizations'
        const volunteersAPI = '/api/volunteers'

        return {
            [ShiftRequestType.SignUpForShift]: (body: any, params: { topLevelShiftId: number }) => `${baseURL}/${params.topLevelShiftId}/register`,
            [ShiftRequestType.UnregisterForShifts]: `${baseURL}/unregister`,
            [ShiftRequestType.RegisterVolunteersForShift]: (body: any, params: { topLevelShiftId: number }) => `${baseURL}/${params.topLevelShiftId}/register`,
            [ShiftRequestType.UpdateVolunteerRegistrationStatuses]: `${baseURL}/update-volunteer-registration-statuses`,
            [ShiftRequestType.GetAllShiftsForOrganization]: (body: any, params: { organizationId: number }) => `${organizationsAPI}/${params.organizationId}/shifts`,
            [ShiftRequestType.GetOrganizationVolunteerShifts]: (body: any, params: { volunteerId: number }) => `${volunteersAPI}/${params.volunteerId}/shifts`,
            [ShiftRequestType.GetShiftDetails]: (body: any, params: { shiftOrParentId: number }) => `${baseURL}/${params.shiftOrParentId}`,
            [ShiftRequestType.PostShift]: `${baseURL}`,
            [ShiftRequestType.PutShift]: (body: any, params: { topLevelShiftId: number }) => `${baseURL}/${params.topLevelShiftId}`,
        };
    }
}

